/* eslint-disable no-nested-ternary */
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { formatEventDateShort } from 'utils/dateTime';
import {
  getDateTimesLabelFromSessionsAvailability,
  getDatesFromSessions,
  areSessionsFree,
} from 'utils/session';
import useEventBookingSession from 'features/Events/hooks/useEventBookingSession';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import { SkeletonLineSlim } from 'components/theme/Skeleton/Skeleton';
import InfoIcon from 'assets/bx-info-circle';
import useWaitList from 'api/waitlist/useWaitList';
import { WaitlistMode } from '@kouto/types';
import useLimitedTimeStatus, {
  TicketSalesState,
} from 'hooks/useLimitedTimeStatus';
import BuyTicketButton, {
  getDateStateFromSessionsAvailability,
} from './BuyTicketButton';

const MAX_DEFAULT_VISIBLE_DATES = 4;

const EventAvailability = () => {
  const { isLoading, listing, eventSessions } = useEventBookingSession();
  const { locale } = useGlobalDateLocale();
  const { data: waitlist } = useWaitList(listing?.waitlistId, listing?.brandId);
  const waitlistEnabled = !!waitlist && waitlist?.mode !== WaitlistMode.NONE;
  const { t: translate } = useTranslation();
  const [showMoreCount, setShowMoreCount] = useState(MAX_DEFAULT_VISIBLE_DATES);

  const limitedTime = useLimitedTimeStatus(
    listing?.limitedTime?.ticketSalesOpenAt,
    listing?.limitedTime?.ticketSalesCloseAt,
  );

  const isClosed = limitedTime?.state === TicketSalesState.CLOSED;
  const isComingSoon = limitedTime?.state === TicketSalesState.NOT_YET_OPEN;

  const dates = useMemo(() => {
    const uniqueDates = getDatesFromSessions(eventSessions);
    return uniqueDates.map((date) => {
      return {
        date,
        isFree: areSessionsFree(eventSessions, date),
        label: formatEventDateShort(date, locale),
        state: getDateStateFromSessionsAvailability(
          eventSessions,
          date,
          waitlistEnabled,
        ),
        timesLabel: getDateTimesLabelFromSessionsAvailability(
          eventSessions,
          date,
          waitlistEnabled,
          locale,
        ),
      };
    });
  }, [eventSessions, waitlistEnabled, locale]);

  if (isLoading || !listing) {
    return (
      <Wrapper>
        <SkeletonLineSlim />
      </Wrapper>
    );
  }

  if (!dates.length) {
    return (
      <Wrapper>
        <NoSessionsMessage>
          <InfoIcon />
          <span>{translate('noDatesAvailable')}</span>
        </NoSessionsMessage>
      </Wrapper>
    );
  }

  return (
    <Wrapper aria-label={translate('bookExperiencePanel')}>
      <ExpandingWrapper>
        {dates
          .slice(0, showMoreCount)
          .map(({ date, isFree, label, state, timesLabel }) => (
            <DateLine
              className="activate-date-line"
              key={date}
              showAsColumn={dates.length === 1}
            >
              <DateLabel>
                <h3 className="activate-date" style={{ margin: 0 }}>
                  {label}
                </h3>
                <span className="activate-time">{timesLabel}</span>
              </DateLabel>
              {isClosed ? null : (
                <BuyTicketButton
                  className="activate-buy-ticket-button"
                  dataTestId={`buy-ticket-on-${date}`}
                  date={date}
                  isFree={isFree}
                  state={state}
                  customLabel={
                    isComingSoon ? translate('comingSoon') : undefined
                  }
                  disabled={isComingSoon}
                />
              )}
            </DateLine>
          ))}
        {showMoreCount < dates.length &&
          dates.length > MAX_DEFAULT_VISIBLE_DATES && (
            <DateLine showAsColumn={false}>
              <ShowMoreLabel
                onClick={() =>
                  setShowMoreCount(showMoreCount + MAX_DEFAULT_VISIBLE_DATES)
                }
              >
                {translate('seeMoreDates')}
              </ShowMoreLabel>
            </DateLine>
          )}
      </ExpandingWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 350px;
  min-height: 150px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ExpandingWrapper = styled.div`
  width: 100%;
  height: auto;
  border: solid 0.5px var(--way-colors-borderColor);
  padding: 0px 16px;

  @media (min-width: 768px) {
    position: sticky;
    top: 100px;
  }
`;

// Heights are minimums, not fixed values: translated labels wrap to two lines in the
// half-width date column (e.g. pt "Sexta-feira, 11 de setembro"), and a pinned height
// pushed .activate-time out through the bottom of the bordered panel. Single-line labels
// still settle at exactly 80px, so English is unaffected.
const DateLine = styled.div<{ showAsColumn: boolean }>`
  width: 100%;
  display: flex;
  min-height: ${({ showAsColumn }) => (showAsColumn ? 'auto' : '80px')};
  padding: ${({ showAsColumn }) => (showAsColumn ? '16px 0px ' : '12px 0px')};
  flex-direction: ${({ showAsColumn }) => (showAsColumn ? 'column' : 'row')};
  gap: ${({ showAsColumn }) => (showAsColumn ? '12px' : '0px')};
  align-items: center;
  border-bottom: solid 0.5px var(--way-colors-borderColor);

  & > *:first-child {
    width: ${({ showAsColumn }) => (showAsColumn ? '100%' : '50%')};
    min-height: ${({ showAsColumn }) => (showAsColumn ? '54px' : '44px')};
  }

  & > *:last-child {
    width: ${({ showAsColumn }) => (showAsColumn ? '100%' : '50%')};
    height: 44px;
    flex-shrink: 0;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const DateLabel = styled.span`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  overflow-wrap: anywhere;

  & > *:first-child {
    color: var(--way-colors-primaryTextColor);
    font-weight: bold;
    font-size: 16px;
  }

  & > *:last-child {
    color: var(--way-colors-contrastColorShades-60);
    font-size: 14px;
  }
`;

const ShowMoreLabel = styled.span`
  width: 100%;
  font-weight: bold;
  cursor: pointer;
  color: var(--way-colors-contrastColorShades-60);
`;

const NoSessionsMessage = styled.div`
  width: 100%;
  height: 48px;
  border: solid 0.5px var(--way-colors-borderColor);
  background-color: var(--way-palette-black-5);
  user-select: none;

  & * {
    display: inline-block;
  }

  & svg {
    margin: 14px;

    & path {
      stroke: none;
      fill: var(--way-colors-svgColor);
    }
  }

  & span {
    line-height: 48px;
    color: var(--way-colors-contrastColorShades-60);
    vertical-align: top;
  }

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

export default EventAvailability;
